import React from "react";
import glamorous from "glamorous";

import {
  ExceptionLink,
  TRAILER_EXCEPTION_TYPES
} from "../exceptions/ExceptionLink";
import { ExceptionDonutChart } from "../exceptions/ExceptionDonutChart";

import Colors from "../../styles/colors";
import { Panel } from "../../vendor/signal-widgets/components/panel";
import {
  LadChicletCSS,
  PartNumberChicletCSS,
  colorForLad
} from "../../components/chiclets";

import EditIcon from "react-icons/lib/md/edit";
import { getKeyFromSiteAndPart } from "./ShipmentsState";

export class WatchlistPanel extends React.Component {
  constructor(props) {
    super(props);
    this.shipmentTotals = { total_shipments: null, details: [] };
  }

  componentDidMount() {
    this.fetchData(this.props.site, this.props.part);
  }

  componentWillReceiveProps(nextProps) {
    // If this panel is assigned a new site or
    // part we will need to fetch the totals
    if (
      nextProps.site !== this.props.site ||
      nextProps.part !== this.props.part
    ) {
      this.fetchData(nextProps.site, nextProps.part);
    } else {
      // It is possible for the owner of this panel
      // to refresh the totals, zeroing them out.
      // When that happens we need to fetch our data
      // over again.  In order to check for that,
      // we hold the totals we are displaying as
      // a member variable, and if we notice that
      // the totals we are about to display have been
      // nulled out, and we have values previoulsy,
      // we will fetch new data
      const { site, part, watchlistShipments } = nextProps;

      const key = getKeyFromSiteAndPart(site, part);

      // Get the key into the watchlist shipments
      const watchlistItem = watchlistShipments.find(d => d.item === key);
      if (watchlistItem !== undefined) {
        // Get our list of shipments from the master watchlist
        // prop which is a list of all watchlist panels, so get the one
        // we need
        const newShipmentTotals = watchlistItem.data;

        // If we had valid totals before, and the incoming
        // ones have been wiped, fetch our data again
        if (
          newShipmentTotals.total_shipments === null &&
          this.shipmentTotals.total_shipments != null
        ) {
          this.fetchData(nextProps.site, nextProps.part);
        }

        this.shipmentTotals = newShipmentTotals;
      }
    }
  }

  fetchData(site, part) {
    const {
      fetchShipmentTotalsByPart,
      fetchShipmentTotalsByLocation
    } = this.props;

    if (part) {
      fetchShipmentTotalsByPart(part);
    } else if (site) {
      fetchShipmentTotalsByLocation(site);
    }
  }

  render() {
    const {
      site,
      locations,
      lads,
      part,
      editWatchlistItem,
      pushSearchView,
      searchShipments,
      setSearchFilter,
      clearSearchFilters,
      setSearchCategory,
      setSearch,
      exceptionTypes
    } = this.props;

    const trailerExceptionLinks = TRAILER_EXCEPTION_TYPES.map((e, i) => (
      <ExceptionLink
        key={i}
        shipmentsTotals={this.shipmentTotals}
        exceptionType={e}
        exceptionTypes={exceptionTypes}
        site={site}
        part={part}
        pushSearchView={pushSearchView}
        searchShipments={searchShipments}
        setSearchFilter={setSearchFilter}
        clearSearchFilters={clearSearchFilters}
        setSearch={setSearch}
        setSearchCategory={setSearchCategory}
        locations={locations}
      />
    ));

    const location = locations.find(l => l.id === site);

    const partTitle = part != null && part.length > 0 ? `Part #${part}` : null;
    let locTitle = site != null && location != null ? location.name : null;
    const title =
      partTitle && locTitle
        ? partTitle + " to " + locTitle
        : partTitle || locTitle;

    let titleChiclet = "Invalid Location";

    if (partTitle) {
      titleChiclet = (
        <PartNumberChicletCSS
          lad={
            location != null
              ? lads.find(l => l.id === Number(location.lad.id))
              : null
          }
          backgroundColor={Colors.background.DARK_BLUE}
          width={45}
          height={45}
        />
      );
    }

    if (location != null && !part) {
      const lad = lads.find(l => l.id === Number(location.lad.id));
      titleChiclet = lad ? (
        <LadChicletCSS lad={lad} width={45} height={45} showLadLabel />
      ) : null;
    }

    const clickableHeader = locTitle != null;

    return (
      <Panel
        title={
          <glamorous.Div
            css={{
              cursor: clickableHeader ? "pointer" : "normal",
              display: "flex",
              flex: 100,
              flexDirection: "row",
              marginRight: "0.5em",
              alignItems: "center",
              fontSize: "small"
            }}
            onClick={
              clickableHeader
                ? () => {
                    clearSearchFilters();
                    setSearchCategory("location");
                    setSearch(location.name, location.id);
                    searchShipments(
                      { label: location.name, value: location.id },
                      "location",
                      {}
                    );
                    pushSearchView();
                  }
                : null
            }
          >
            <glamorous.Div
              css={{
                margin: "0.5em",
                marginLeft: "0.25em",
                borderRadius: "3px",
                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)"
              }}
            >
              {titleChiclet}
            </glamorous.Div>
            {title}
          </glamorous.Div>
        }
        size="third"
        canMaximize={false}
        style={{ border: "none" }}
        headerComponents={
          <glamorous.Div
            css={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              ":hover": { cursor: "pointer", color: "#ccc" }
            }}
            onClick={() => editWatchlistItem()}
          >
            <EditIcon />
          </glamorous.Div>
        }
        headerStyle={{
          backgroundColor:
            location != null
              ? colorForLad(lads.find(l => l.id === Number(location.lad.id)))
              : Colors.background.DARK_BLUE,
          color: "white",
          fontWeight: "300"
        }}
      >
        <glamorous.Div
          css={{
            display: "flex",
            flexDirection: "row",
            height: "100%",
            alignItems: "flex-start"
          }}
        >
          <glamorous.Div
            css={{ display: "flex", flexDirection: "column", flex: 1 }}
          >
            {trailerExceptionLinks}
          </glamorous.Div>
          <glamorous.Div css={{ display: "flex", flex: 1 }}>
            <ExceptionDonutChart
              shipmentsTotals={this.shipmentTotals}
              site={site}
              part={part}
              pushSearchView={pushSearchView}
              searchShipments={searchShipments}
              setSearchFilter={setSearchFilter}
              clearSearchFilters={clearSearchFilters}
              setSearch={setSearch}
              setSearchCategory={setSearchCategory}
              locations={locations}
            />
          </glamorous.Div>
        </glamorous.Div>
      </Panel>
    );
  }
}

export default WatchlistPanel;



// WEBPACK FOOTER //
// ./src/modules/shipment-status/WatchlistPanel.js